import { useEffect, useRef } from "react";
import { ParticipationEventState } from "@Interfaces";

const WAITING_POLL_INTERVAL_MS = 60000;
const COLLECTING_POLL_INTERVAL_MS = 30000;

/**
 * Re-runs `onPoll` on a state-dependent interval while the tab is visible:
 * ~60s while waiting for the first answer, ~30s while collecting. Pauses
 * when the browser tab is hidden and stops for good once the event closes.
 */
const useParticipationPolling = (
    state: ParticipationEventState | undefined,
    onPoll: () => void
) => {
    const onPollRef = useRef(onPoll);
    onPollRef.current = onPoll;

    useEffect(() => {
        if (!state || state === "closed") return undefined;

        const intervalMs =
            state === "waiting"
                ? WAITING_POLL_INTERVAL_MS
                : COLLECTING_POLL_INTERVAL_MS;

        let intervalId: ReturnType<typeof setInterval> | null = null;

        const start = () => {
            if (intervalId) return;
            intervalId = setInterval(() => onPollRef.current(), intervalMs);
        };

        const stop = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stop();
            } else {
                // Numbers may be hours old after a hidden tab; refresh right away.
                onPollRef.current();
                start();
            }
        };

        if (!document.hidden) {
            start();
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            stop();
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [state]);
};

export default useParticipationPolling;
